import PlatformLinks from "@/components/PlatformLinks";
import SEO from "@/components/SEO";
import CtaBanner from "@/components/CtaBanner";
import { MarketplaceGrid } from "@/components/MarketplaceLogos";
import { QUICK_COMMERCE } from "@/data/content";
import { Zap, AlertTriangle, CheckCircle2 } from "lucide-react";

const CHALLENGES = [
  { title: "Inventory management", desc: "10-min delivery means dark stores must always have your stock. Stock-outs kill ranking instantly." },
  { title: "SKU selection", desc: "Quick commerce rewards small pack sizes, impulse-priced SKUs and high-velocity essentials." },
  { title: "Pricing strategy", desc: "Margins are tighter than marketplaces — pricing must work even after platform fees and free deliveries." },
];

const SOLUTIONS = [
  { title: "Vendor onboarding", desc: "We complete vendor registration with Blinkit, Zepto and Instamart end-to-end." },
  { title: "SKU strategy", desc: "We design pack sizes and SKU mix optimised for impulse buys and basket math." },
  { title: "Inventory planning", desc: "City-wise demand forecasting and dark-store replenishment workflows." },
  { title: "Growth strategy", desc: "Promotions, banner placements, and category-manager relationships to scale velocity." },
];

export default function QuickCommerce() {
  return (
    <>
      <SEO
        title="Quick Commerce — Blinkit, Zepto & Instamart Onboarding"
        description="Quick commerce vendor onboarding for Blinkit, Zepto and Instamart. SKU strategy, inventory planning, and growth management for India's 10-minute delivery channels."
        path="/quick-commerce"
      />

      <section className="border-b-2 border-black bg-[#FFD700] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="tape-dark">Quick Commerce</div>
          <h1 className="display mt-6 max-w-4xl text-5xl font-black leading-[0.95] tracking-tight md:text-6xl lg:text-7xl">
            Sell Faster.<br /><span className="bg-black px-2 text-[#FFD700]">Deliver Faster.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-black/80 md:text-lg">
            Blinkit, Zepto and Instamart deliver in 10–30 minutes. The model rewards fast-moving SKUs, sharp pricing and disciplined inventory.
          </p>
          <div className="mt-8 inline-flex items-center gap-2 border-2 border-black bg-white px-4 py-2 shadow-brutal-sm">
            <Zap size={16} className="text-black" />
            <span className="text-sm font-bold uppercase tracking-widest">10-minute delivery zones</span>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="eyebrow">The Channels</div>
          <h2 className="display mt-3 text-3xl font-black tracking-tight md:text-4xl">Four platforms. Massive velocity.</h2>
          <div className="mt-10">
            <MarketplaceGrid items={QUICK_COMMERCE} />
          </div>
        </div>
      </section>

      <section className="border-y-2 border-black bg-[#FAFAF7] py-20 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 md:grid-cols-2 md:px-8">
          <div>
            <div className="eyebrow">The Challenges</div>
            <h2 className="display mt-3 text-3xl font-black tracking-tight md:text-4xl">Why quick commerce is different.</h2>
            <div className="mt-8 space-y-4">
              {CHALLENGES.map((c) => (
                <div key={c.title} className="card-brutal flex gap-4 p-6">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center border-2 border-black bg-white">
                    <AlertTriangle size={18} />
                  </div>
                  <div>
                    <h3 className="display text-lg font-black">{c.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-black/70">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="eyebrow text-[#a08800]">The Orina Solution</div>
            <h2 className="display mt-3 text-3xl font-black tracking-tight md:text-4xl">How we operate it.</h2>
            <div className="mt-8 space-y-4">
              {SOLUTIONS.map((s) => (
                <div key={s.title} className="card-brutal flex gap-4 !bg-[#FFD700] p-6">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center border-2 border-black bg-black text-[#FFD700]">
                    <CheckCircle2 size={18} />
                  </div>
                  <div>
                    <h3 className="display text-lg font-black">{s.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-black/80">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <PlatformLinks groups={["quick"]} title="Quick-commerce services" />

      <CtaBanner
        title="Want to win quick commerce?"
        subtitle="Onboard with Blinkit, Zepto and Instamart in under 30 days. Free strategy call."
      />
    </>
  );
}
